import React, { useMemo, useState } from "react";
import { useCompanyContext } from "../hooks/useCompanyContext";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import axios from "axios";

const Transport = () => {
  const { company } = useCompanyContext();
  const { t } = useCompanyContext();
  const { cultivationCompany } = useCultivationCompanyContext();
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([
    { id: "Delivery_Date", desc: true },
    { id: "No", desc: true },
  ]);


  const dateTimeSort = (rowA, rowB) => {
    const dateA = rowA.original.Delivery_Date;
    const timeA = rowA.original.Delivery_Time || "00:00:00";
  
    const dateB = rowB.original.Delivery_Date;
    const timeB = rowB.original.Delivery_Time || "00:00:00";
  
    const a = new Date(`${dateA}T${timeA}`);
    const b = new Date(`${dateB}T${timeB}`);
  
    return a - b;
  };

  // Functie om de transport-PDF op te halen en in een nieuwe tab te openen
  const handleDownload = async (no) => {
    if (!cultivationCompany || !cultivationCompany.name) {
      alert("Geen geldig bedrijf geselecteerd.");
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/transports/pdf/${no}`,
        {
          responseType: "blob",
          headers: {
            "X-Cultivation-Company": cultivationCompany.name,
          },
        }
      );

      const fileURL = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const newTab = window.open(fileURL, "_blank");

      if (!newTab) {
        alert(
          "Pop-ups zijn geblokkeerd door de browser. Sta pop-ups toe om de PDF te openen."
        );
        return;
      }

      newTab.onload = () => {
        newTab.document.title = `${no}.pdf`;
      };

      setTimeout(() => {
        window.URL.revokeObjectURL(fileURL);
      }, 5000);
    } catch (error) {
      console.error("Fout bij ophalen van PDF:", error);
      alert("Er is een fout opgetreden bij het ophalen van de transport PDF.");
    }
  };

  // Filter transports op basis van zoekterm
  const filteredTransports = useMemo(() => {
    if (!company?.transports) return [];

    const searchTerm = search.trim().toLowerCase();

    if (!searchTerm) return company.transports;

    return company.transports.filter((transport) => {
      // Bestaande zoekfunctie over alle transportvelden
      const matchesOtherFields = Object.values(transport).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm)
      );

      // Datumvelden voor WPK en P. van Geest
      const dateFields = [
        transport.Delivery_Date,
        transport.Promised_Delivery_Date,
      ];

      // Zoeken op Nederlandse datumnotatie
      const matchesDate = dateFields.some((dateValue) => {
        if (!dateValue) return false;

        const date = new Date(dateValue);

        if (Number.isNaN(date.getTime())) return false;

        const formattedDate = date.toLocaleDateString("nl-NL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

        return formattedDate.includes(searchTerm);
      });

      return matchesOtherFields || matchesDate;
    });
  }, [company?.transports, search]);

  // Kolommen definitie
  const columns = useMemo(
    () => [
      {
        accessorKey: "No",
        header: "Nr.",
      },
      {
        id: "actions",
        header: `${t.afleverbon} / ${t.plantenpaspoort}`,
        cell: ({ row }) => (
          <button
            onClick={() => handleDownload(row.original.No)}
            className="download-button"
          >
            {t.afleverbon}
          </button>
        ),
      },
      ...(cultivationCompany?.name === "P. van Geest"
        ? [
            {
              accessorKey: "Sell_to_Customer_No",
              header: t.bedrijf,
            },
            {
              accessorKey: "Ship_to_Name",
              header: t.naam,
            },
            {
              accessorKey: "Promised_Delivery_Date",
              header: t.leverdatum,
              cell: (info) => {
                const dateValue = info.getValue();
                return dateValue
                  ? new Date(dateValue).toLocaleDateString("nl-NL", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : "N.v.t.";
              },
            },
          ]
        : []),
      ...(cultivationCompany?.name === "WPK"
        ? [
            {
              accessorKey: "Description",
              header: t.omschrijving,
              meta: { className: "col-description" },
            },
            {
              accessorKey: "Ship_to_Name",
              header: t.verzendnaam,
              meta: { className: "col-verzendnaam" },
            },
            {
              accessorKey: "Delivery_Date",
              header: t.afleverdatum,
              meta: { className: "col-afleverdatum" },
              cell: (info) => {
                const dateValue = info.getValue();
                return dateValue
                  ? new Date(dateValue).toLocaleDateString("nl-NL", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : "N.v.t.";
              },
              sortingFn: dateTimeSort,
            },
            {
              accessorKey: "Delivery_Time",
              header: t.tijd,
              meta: { className: "col-verzenddatum" },
              cell: (info) => {
                const timeValue = info.getValue();
              
                if (!timeValue || timeValue === "00:00:00") {
                  return "-";
                }
              
                return timeValue.slice(0, 5);
              },
            },
            {
              accessorKey: "Transport_No",
              header: t.ritnummer,
              meta: { className: "col-ritnummer" },
            },
            {
              accessorKey: "Shipping_Agent_Code",
              header: t.expediteurnaam,
              meta: { className: "col-expediteur" },
            },
          ]
        : []),
    ],
    [t]
  );

  // Tabel logica
  const table = useReactTable({
    data: filteredTransports,
    state: {
      sorting,
    },
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <div className="transport-container">
      <h3 className="pageheading">{t.transport}</h3>

      <div className="search-container">
        <input
          type="text"
          placeholder={t.zoekPlaceholderTransport}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      <table className="transport-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={header.column.columnDef.meta?.className || ""}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={cell.column.columnDef.meta?.className || ""}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {t.pagination.previous}
        </button>
        <span>
        {t.pagination.page}{" "}
          <strong>
            {table.getState().pagination.pageIndex + 1} {t.pagination.of}{" "}
            {table.getPageCount()}
          </strong>
        </span>
        <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
        {t.pagination.next}
        </button>
      </div>

      <style>
        {`
          /* Tablet en kleiner */
          @media (max-width: 1100px) {
            .col-description { display: none; }
          }

          /* Mobiel landschap */
          @media (max-width: 900px) {
          }

          /* Mobiel portret */
          @media (max-width: 700px) {
            .col-laaddatum { display: none; }
            .col-afleverdatum { display: none; }

            .transport-table th,
            .transport-table td {
              padding: 6px 8px;
              font-size: 13px;
            }
          }

          /* Altijd: voorkom overflow issues */
          .transport-container {
            overflow-x: auto;
          }
        `}
      </style>
    </div>
  );
};

export default Transport;
